/**
 * SceneItemsSection - Items + interactable objects in the current scene.
 *
 * Rendered inside ScenePanel below the chars section. Items are grouped by name
 * (e.g., "Healing Potion x3"). Objects (doors, chests, levers, traps) get state
 * badges. Hidden when both lists are empty.
 */

import { useShallow } from 'zustand/react/shallow';
import { Badge } from '@/components/ui/badge';
import { Package, DoorOpen, Box, Zap, ToggleLeft, Circle } from 'lucide-react';
import { useGameStore } from '@/lib/stores/gameStore';
import type { SceneInteractable } from '@danda/shared';
import { CLOSABLE_INTERACTABLE_TYPES } from '@danda/shared';
import { submitTapIntent } from '@/lib/intent/submit-tap-intent';
import { formatGoldFromCopper, isCoinsItem } from './SceneItemsSection.helpers';

function getInteractableIcon(type: SceneInteractable['type']) {
  switch (type) {
    case 'door':
      return <DoorOpen className="h-3 w-3 text-amber-500" />;
    case 'chest':
      return <Box className="h-3 w-3 text-yellow-500" />;
    case 'lever':
    case 'switch':
      return <ToggleLeft className="h-3 w-3 text-blue-500" />;
    case 'trap':
      return <Zap className="h-3 w-3 text-red-500" />;
    default:
      return <Circle className="h-3 w-3 text-gray-500" />;
  }
}

function getStateBadge(interactable: SceneInteractable) {
  switch (interactable.state) {
    case 'open':
      return { label: 'OPEN', className: 'bg-green-600 text-white' };
    case 'closed':
      // Show locked state when closed — an open door is never "locked"
      return interactable.locked
        ? { label: 'LOCKED', className: 'bg-red-600 text-white' }
        : { label: 'CLOSED', className: 'bg-zinc-600 text-white' };
    case 'locked':
      return { label: 'LOCKED', className: 'bg-red-600 text-white' };
    case 'activated':
      return { label: 'ACTIVATED', className: 'bg-blue-600 text-white' };
    case 'deactivated':
      return { label: 'DEACTIVATED', className: 'bg-zinc-600 text-white' };
    case 'triggered':
      return { label: 'TRIGGERED', className: 'bg-orange-600 text-white' };
    case 'looted':
      return { label: 'LOOTED', className: 'bg-amber-700 text-white' };
    case 'destroyed':
      return { label: 'DESTROYED', className: 'bg-red-900 text-white' };
    default:
      return {
        label: String(interactable.state).toUpperCase(),
        className: 'bg-zinc-600 text-white',
      };
  }
}

export default function SceneItemsSection() {
  const { sceneItems, sceneInteractables } = useGameStore(
    useShallow((s) => ({
      sceneItems: s.sceneItems,
      sceneInteractables: s.sceneInteractables,
    }))
  );

  if (sceneItems.length === 0 && sceneInteractables.length === 0) {
    return null;
  }

  const groupedItems = new Map<
    string,
    {
      id: string;
      name: string;
      emoji?: string;
      quantity: number;
      description?: string;
      lootable: boolean;
    }
  >();
  for (const item of sceneItems) {
    const existing = groupedItems.get(item.name);
    if (existing) {
      existing.quantity += item.quantity ?? 1;
      if (existing.description !== item.description) {
        existing.description = undefined;
      }
      // Prefer a lootable instance's id so a tap on a mixed stack picks up a
      // takeable one even if a non-lootable duplicate was inserted first.
      if (!existing.lootable && item.lootable) {
        existing.id = item.id;
        existing.lootable = true;
      }
    } else {
      groupedItems.set(item.name, {
        id: item.id,
        name: item.name,
        emoji: (item as Record<string, unknown>).emoji as string | undefined,
        quantity: item.quantity ?? 1,
        description: item.description,
        lootable: item.lootable,
      });
    }
  }

  return (
    <div className="space-y-3">
      {groupedItems.size > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
            Items
          </h4>
          <div className="space-y-1">
            {[...groupedItems.values()].map((item) => {
              // Coins are a special case — `quantity` is the loot pile's total
              // value in COPPER pieces (combat_end.rs aggregates each monster's
              // `gold_cp` directly into the pile). Showing `x1300` reads as
              // "1300 coins" but mechanically that's 13 GP (1300 CP / 100).
              // Format the quantity as GP/CP so the panel matches the pickup
              // narrative ("Bran picks up 13 GP"). Scenario 2026-05-12 B6.
              const isCoins = isCoinsItem(item.name);
              const quantityLabel = isCoins
                ? formatGoldFromCopper(item.quantity)
                : item.quantity > 1
                  ? `x${item.quantity}`
                  : null;
              const rowInner = (
                <>
                  {item.emoji ? (
                    <span className="text-base flex-shrink-0">{item.emoji}</span>
                  ) : (
                    <Package className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                  )}
                  <span className="text-sm truncate flex-1 text-left">{item.name}</span>
                  {quantityLabel && (
                    <Badge
                      variant="secondary"
                      className={
                        isCoins
                          ? 'border-transparent text-xs px-1.5 py-0 uppercase tracking-wide bg-yellow-500/40 text-foreground dark:text-yellow-100'
                          : 'text-xs px-1.5 py-0'
                      }
                    >
                      {quantityLabel}
                    </Badge>
                  )}
                </>
              );
              // Lootable items are tappable — fire a fully-structured PickUp
              // intent by id (no LLM text fallback). Non-lootable display rows
              // (fixed scenery loot) stay inert.
              return item.lootable ? (
                <button
                  key={item.name}
                  type="button"
                  onClick={() =>
                    submitTapIntent(
                      { intent_type: 'pick_up', item_id: item.id, item_name: item.name },
                      `Pick up ${item.name}`
                    )
                  }
                  title={`Pick up ${item.name}`}
                  className="flex w-full items-center gap-2 py-1 px-1 -mx-1 rounded transition-colors cursor-pointer hover:bg-muted/60 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-500"
                >
                  {rowInner}
                </button>
              ) : (
                <div key={item.name} className="flex items-center gap-2 py-1">
                  {rowInner}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {sceneInteractables.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
            Objects
          </h4>
          <div className="space-y-1">
            {sceneInteractables.map((obj) => {
              const showState = (CLOSABLE_INTERACTABLE_TYPES as readonly string[]).includes(
                obj.type
              );
              const stateBadge = showState ? getStateBadge(obj) : null;
              // Objects are tappable — fire a fully-structured Investigate
              // intent by object id (same wire shape as `/investigate`).
              return (
                <button
                  key={obj.id}
                  type="button"
                  onClick={() =>
                    submitTapIntent(
                      { intent_type: 'investigate', target: obj.id },
                      `Investigate ${obj.name}`
                    )
                  }
                  title={`Investigate ${obj.name}`}
                  className="flex w-full items-center gap-2 py-1 px-1 -mx-1 rounded transition-colors cursor-pointer hover:bg-muted/60 focus:outline-none focus-visible:ring-1 focus-visible:ring-teal-500"
                >
                  {getInteractableIcon(obj.type)}
                  <span className="text-sm truncate flex-1 text-left">{obj.name}</span>
                  {stateBadge && (
                    <span
                      className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${stateBadge.className}`}
                    >
                      {stateBadge.label}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
